<template>
    <div class="mx-3">
        <archive-information-modal @save="updateArchiveNote"></archive-information-modal>
        <div class="d-flex justify-content-between align-items-end border-bottom">
            <h4>{{ client.name }}</h4>
            <h1 class="text-muted font-italic">Archived Claims</h1>
            <div class="btn-toolbar mb-1" role="toolbar">
                <div class="input-group mr-2">
                    <div class="input-group-prepend">
                        <div class="input-group-text"><i class="fas fa-search fa-lg text-muted"></i></div>
                    </div>
                    <input type="text" class="form-control" placeholder="Search" v-model="searchString">
                </div>
                <div class="btn-group">
                    <a :href="'/attorney/clients/' + client.id + '/claims'" class="btn btn-primary" data-toggle="tooltip" title="Active Claim List">
                        <i class="fas fa-list fa-lg"></i>
                    </a>
                </div>
            </div>            
        </div>
        <div v-if="filteredClaims.length" class="table-responsive">
            <table class="table table-striped table-sm">
                <thead>
                    <tr>
                        <th>Docket Number(s)</th>
                        <th>Claim Number</th>
                        <th>Patient Name</th> 
                        <th>Date of Injury</th>                                 
                        <th>Provider</th>
                        <th>Payer</th>
                        <th>Shared</th>
                        <th>Archived</th>
                        <th class="text-right"><div class="multiline-header">Estimated<br />Reimbursement</div></th>
                        <th class="text-right">Balance</th>
                        <th></th>
                    </tr>                    
                </thead>
                <tbody>
                    <tr v-for="claim in paginatedClaims" :key="'archived-claim-' + claim.id">
                        <td>{{ claim.docket_numbers }}</td>
                        <td>{{ claim.claim_number }}</td>
                        <td>{{ claim.patient_name }}</td>
                        <td>{{ claim.injury_date }}</td>
                        <td>{{ claim.rendering_provider_name }}</td>
                        <td>{{ claim.payer_name }}</td>
                        <td>{{ claim.shared_at }}</td>
                        <td>{{ claim.archived_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                        <td class="text-right">{{ claim.estimated_reimbursement | currency }}</td>
                        <td class="text-right">{{ claim.balance| currency }}</td>
                        <td>
                            <div class="d-flex flex-nowrap flex-row justify-content-end">

                                <div class="flex-fill text-center">
                                    <button class="btn btn-link p-0 m-0" title="Archive Information" @click="showInfo(claim)">
                                        <i class="fas fa-info-circle fa-lg fa-fw"></i>
                                    </button>                                    
                                </div>

                                <div class="flex-fill text-center">
                                    <button class="btn btn-link btn-sm p-0 m-0" title="Unarchive Claim" @click="unarchive(claim.shared_attorney_claim_id)">
                                        <div class="fa-stack">
                                            <i class="fas fa-archive fa-stack-1x"></i>
                                            <i class="fas fa-arrow-circle-up fa-stack-1x fa-inverse unarchive-icon"></i>
                                        </div> 
                                    </button>
                                </div>

                            </div>                            
                        </td>                        
                    </tr>
                </tbody>
            </table>
            <div class="d-flex">
                <nav v-if="pageCount > 1">
                    <ul class="pagination">
                        <li v-bind:class="['page-item', { 'disabled' : pageNumber == 1 }]">
                            <a class="page-link" href="#" @click="first()">
                                <span id="first-page"><i class="fas fa-step-backward fa-xs chev-pos"></i></span>
                            </a>
                        </li>                        
                        <li :class="['page-item', { 'disabled' : pageNumber == 1 }]">
                            <a class="page-link" href="#" @click="previous()">
                                <span id="previous"><i class="fas fa-chevron-left fa-xs chev-pos"></i><i class="fas fa-chevron-left fa-xs mr-1 chev-pos"></i>Previous</span>
                            </a>
                        </li>
                        <li v-bind:class="['page-item', { 'disabled' : pageNumber == pageCount }]">
                            <a class="page-link" href="#" @click="next()">
                                <span id="next">Next<i class="fas fa-chevron-right fa-xs ml-2 chev-pos"></i><i class="fas fa-chevron-right fa-xs chev-pos"></i></span>
                            </a>
                        </li>
                        <li v-bind:class="['page-item', { 'disabled' : pageNumber == pageCount }]">
                            <a class="page-link" href="#" @click="last()">
                                <span id="first-page"><i class="fas fa-step-forward fa-xs chev-pos"></i></span>
                            </a>
                        </li>                        
                    </ul>
                </nav>
                <div class="ml-auto"> Page {{ pageNumber }} of {{ pageCount }}</div>
            </div>
                                     
        </div>
        <div v-else-if="isLoading" class="pt-3">            
            <h3 class="text-muted text-center"><i class="fas fa-spinner fa-lg fa-spin align-middle"></i> Loading...</h3>
        </div>
        <div v-else class="pt-3">
            <h2 class="text-muted text-center">No archived claims.</h2>
        </div>
    </div>
</template>

<script>
import ArchiveInformationModal from '../ArchiveInformationComponent.vue';

export default {
    components: {
        ArchiveInformationModal
    },
    props: {
        client: {
            type: Object,
            required: true,
            default: null
        },
        size: {
            type: Number,
            required: true,
            default: 15
        }
    },
    data() {
        return {
            searchString: '',
            pageNumber: 1 ,
            isLoading: false,
            claims: []            
        }
    },
    computed: {
        pageCount: function () {
            let length = this.filteredClaims.length;
            return Math.ceil(length / this.size);
        },
        filteredClaims: function () {
            var self = this;

            // filter on search string
            var retval = self.claims.filter(claim => {
                return ((claim.patient_name.toLowerCase().indexOf(self.searchString.toLowerCase()) !== -1) ||
                    (claim.docket_numbers.toLowerCase().indexOf(self.searchString.toLowerCase()) !== -1));
            });

            return retval;
        },
        paginatedClaims: function () {
            const start = (this.pageNumber - 1) * this.size;
            const end = start + this.size;

            return this.filteredClaims.slice(start, end);
        }
    },    
    methods: {
        refresh: function () {
            this.isLoading = true;
            axios.get('/ajax/attorney/clients/' + this.client.id + '/archive/claims')
                .then(response => {
                    this.claims = response.data;
                    this.isLoading = false;
                })
                .catch(error => {
                    console.log(error);
                    this.isLoading = false;
                });
        },
        first: function () {
            if (this.pageNumber > 1) {
                this.pageNumber = 1;
            }
        },        
        previous: function () {
            if (this.pageNumber > 1) {            
                this.pageNumber--;
            }
        },
        next: function () {
            if (this.pageNumber < this.pageCount) {            
                this.pageNumber++;
            }
        },
        last: function () {
            if (this.pageNumber < this.pageCount) {
                this.pageNumber = this.pageCount;
            }
        },
        unarchive: function (id) {
            axios.patch('/ajax/attorney/clients/' + this.client.id + '/archive/claims/' + id)
                .then(response => {
                    // nothing to do; event will update ui 
                })
                .catch(error => {
                    toastr['error'](error.response.data.message, 'Error', { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
                });
        },
        showInfo: function (claim) {
            eventHub.$emit('show-archive-information', claim);
        },
        updateArchiveNote: function (claim) {
            axios.patch(`/ajax/attorney/clients/${this.client.id}/archive/claims/${claim.shared_attorney_claim_id}/note`, {
                note: claim.archive_note
            })
            .then(response => { 

            })
            .catch(error => {
                toastr['error'](error.response.data.message, 'Error', { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
            });
        }              
    },
    created() {
        this.refresh();

        window.Echo.private('shared-claim-event.' + this.client.id)
            .listen('SharedClaimEvent', (e) => {
                this.refresh(); 
            });        
    }        
}
</script>

<style scoped>
    .unarchive-icon {
        padding-top: 3px;
        font-size: .85em;
    }

    .multiline-header {
        display: inline-block;
        text-align: left;
    }    
</style>